import {
  ActivityLogIcon,
  ArchonIcon,
  BarChartIcon,
  CalendarIcon,
  CardStackIcon,
  ChatBubbleIcon,
  CheckCircledIcon,
  CodeIcon,
  ColumnsIcon,
  CounterClockwiseClockIcon,
  DashboardIcon,
  ExclamationTriangleIcon,
  FileIcon,
  FileTextIcon,
  GearIcon,
  IconSize,
  LightningBoltIcon,
  ListBulletIcon,
  PersonIcon,
  Share1Icon,
  TargetIcon,
  TokensIcon,
} from '@protoss/ui';
import * as React from 'react';

/**
 * Maps iconKey strings (stored in project_menu_item.icon_key) to rendered icon nodes.
 * All icons are rendered at IconSize.md with aria-hidden="true".
 *
 * When adding a new menu item icon:
 *  1. Add the iconKey → JSX entry here
 *  2. Export the new icon from packages/ui/src/icons/index.ts if not already present
 *  3. Document in context/iconography.md § 5
 */
const ICON_MAP: Record<string, React.ReactNode> = {
  'activity-log': <ActivityLogIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'bar-chart': <BarChartIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'card-stack': <CardStackIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'chat-bubble': <ChatBubbleIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'check-circled': <CheckCircledIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  code: <CodeIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  columns: <ColumnsIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'counter-clockwise-clock': (
    <CounterClockwiseClockIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
  ),
  dashboard: <DashboardIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'exclamation-triangle': (
    <ExclamationTriangleIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
  ),
  file: <FileIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'file-text': <FileTextIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  gear: <GearIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'lightning-bolt': (
    <LightningBoltIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
  ),
  'list-bullet': <ListBulletIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  person: <PersonIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  'share-1': <Share1Icon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  calendar: <CalendarIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  microphone: <ArchonIcon size={IconSize.md} isRunning />,
  target: <TargetIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
  tokens: <TokensIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
};

/**
 * Resolve a project menu iconKey to a rendered React icon node.
 *
 * Falls back to a DashboardIcon when the key is not found in the registry
 * so that newly added DB entries don't crash the UI.
 */
export function resolveProjectMenuIcon(iconKey: string): React.ReactNode {
  return (
    ICON_MAP[iconKey] ?? (
      <DashboardIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
    )
  );
}
